import { CommonModule } from '@angular/common';
import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { AccountServiceBase, UpdatePasswordModel } from '@seahorse/auth';
import { NotificationService } from '@seahorse/common';
import { SeahorseFormsModule } from '@seahorse/forms';
import { SeahorseLayoutModule } from '@seahorse/layout';
import {
  ButtonComponent,
  LanguagePickerComponent,
  SeahorseUiModule,
} from '@seahorse/ui';
import { Subscription } from 'rxjs';
import { createResetPasswordForm } from './reset-password-form';

@Component({
  selector: 'cp-reset-password-page',
  templateUrl: './reset-password-page.html',
  styleUrls: ['./reset-password.page.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterModule,
    TranslateModule,
    SeahorseFormsModule,
    SeahorseLayoutModule,
    ButtonComponent,
    LanguagePickerComponent,
    SeahorseUiModule,
  ],
})
export class ResetPasswordPage implements OnInit, OnDestroy {
  isLoading = false;
  _subscriptions = new Subscription();

  private _accountService = inject(AccountServiceBase);
  private _notificationService = inject(NotificationService);
  private _activatedRoute = inject(ActivatedRoute);
  private _router = inject(Router);

  form = createResetPasswordForm();

  resetPasswordKey: string | null;

  ngOnInit(): void {
    this.resetPasswordKey =
      this._activatedRoute.snapshot.queryParamMap.get('key');

    if (!this.resetPasswordKey) {
      this._notificationService.showError(
        'account.resetPassword.error.invalidToken',
        'shared.terms.error'
      );

      this._router.navigate(['/account/login']);
      return;
    }
  }

  resetPassword() {
    this.form.submit();

    if (!this.form.valid) {
      return;
    }

    const encryptedKey = this.resetPasswordKey;
    const newPassword = this.form.get('newPassword')?.value;

    const data = new UpdatePasswordModel(encryptedKey, newPassword);

    this.isLoading = true;
    this._subscriptions.add(
      this._accountService.updatePassword(data).subscribe({
        next: (res) => {
          if (!res) {
            this._notificationService.showError('shared.terms.failedMessage');
          }

          this._notificationService.showSuccess(
            'account.resetPassword.success'
          );
          this._router.navigate(['/account/login']);
        },
        error: () => {
          this._notificationService.showError('shared.terms.failedMessage');
        },
        complete: () => {
          this.isLoading = false;
        },
      })
    );
  }

  ngOnDestroy(): void {
    this._subscriptions.unsubscribe();
  }
}
